/**
 * „Was ist neu" — Release-Notes beim ersten Login nach einem Update.
 *
 * Warum überhaupt: das Portal ändert sich wöchentlich, und Funktionen, von denen
 * niemand weiß, existieren praktisch nicht. Der geführte Rundgang erklärt das
 * Ganze für Neue; dieses Modal erklärt den Bestandsnutzern das Delta.
 *
 * Der Merker liegt in localStorage und je Nutzer, damit auf einem geteilten
 * Werkstatt-Rechner nicht der eine für den anderen wegklickt. Ohne localStorage
 * (Privatmodus) funktioniert alles weiter — es merkt sich dann nichts, das ist
 * besser als ein Absturz.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconSparkles,
  IconTicket,
  IconRobot,
  IconMessage,
  IconClock,
  IconBuildingWarehouse,
  IconUsers,
  IconShieldCheck,
  IconFileText,
  IconChartBar,
  IconMail,
  IconSearch,
  IconBriefcase2,
  IconCamera,
  IconChecklist,
  IconCalendarEvent,
} from '@tabler/icons-react';
import { useAuthStore } from '../../stores/auth';
import {
  CHANGELOG,
  compareVersions,
  getUnseenEntries,
  type ChangelogEntry,
  type ChangelogIcon,
} from '../../onboarding/changelog';

const STORAGE_PREFIX = 'servicehub.changelog.lastSeen';
const OPEN_EVENT = 'servicehub:open-changelog';

/** Von überall aufrufbar — Benutzermenü, Seitenleisten-Fuß, Hilfe-Seite. */
export function openChangelog(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

function storageKey(userId?: string | number | null): string {
  return userId ? `${STORAGE_PREFIX}.${userId}` : STORAGE_PREFIX;
}

function readLastSeen(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLastSeen(key: string, version: string): void {
  try {
    localStorage.setItem(key, version);
  } catch {
    // Kein localStorage (Privatmodus) — dann erscheint der Hinweis eben erneut.
  }
}

const ICONS: Record<ChangelogIcon, typeof IconTicket> = {
  ticket: IconTicket,
  kian: IconRobot,
  chat: IconMessage,
  clock: IconClock,
  warehouse: IconBuildingWarehouse,
  customer: IconUsers,
  shield: IconShieldCheck,
  file: IconFileText,
  chart: IconChartBar,
  mail: IconMail,
  search: IconSearch,
  project: IconBriefcase2,
  camera: IconCamera,
  task: IconChecklist,
  calendar: IconCalendarEvent,
};

/** Merge-Zeitpunkte sind Berliner Zeit — auch für jemanden, der gerade im Ausland ist. */
const RELEASE_TIME_ZONE = 'Europe/Berlin';

/** „11. September 2026, 16:24 Uhr" — mehrere Releases am Tag sind sonst nicht auseinanderzuhalten. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: RELEASE_TIME_ZONE,
  });
  // Ein Eintrag nur mit Datum bekäme sonst eine erfundene Uhrzeit (Mitternacht UTC).
  if (!iso.includes('T')) return day;
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: RELEASE_TIME_ZONE });
  return `${day}, ${time} Uhr`;
}

/** „August 2026" — Trenner in der Voll-Ansicht, damit 35 Einträge navigierbar bleiben. */
function monthLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: RELEASE_TIME_ZONE });
}

function EntryCard({ entry, isAdmin }: { entry: ChangelogEntry; isAdmin: boolean }) {
  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      bg={entry.highlight ? 'var(--mantine-color-orange-light)' : undefined}
    >
      <Stack gap="sm">
        <Group gap="xs" wrap="wrap">
          <Badge size="sm" variant={entry.highlight ? 'filled' : 'light'} color="orange">
            v{entry.versionLabel ?? entry.version}
          </Badge>
          <Text size="xs" c="dimmed">
            {formatDate(entry.date)}
          </Text>
          {entry.adminOnly && (
            <Badge size="xs" variant="outline" color="gray">
              Nur Admin
            </Badge>
          )}
        </Group>

        <div>
          <Title order={4} fz="h5">
            {entry.title}
          </Title>
          {entry.summary && (
            <Text size="sm" c="dimmed" mt={2}>
              {entry.summary}
            </Text>
          )}
        </div>

        <Stack gap="sm">
          {/* Punkte ohne Technikerzugriff bleiben weg — eine Liste voller Dinge, die
              man nicht bedienen kann, entwertet die übrigen. */}
          {entry.items
            .filter((item) => !item.adminOnly || isAdmin)
            .map((item) => {
            const Icon = item.icon ? ICONS[item.icon] : IconSparkles;
            return (
              <Group key={item.title} gap="sm" align="flex-start" wrap="nowrap">
                <ThemeIcon size="md" radius="md" variant="light" color="orange" mt={2}>
                  <Icon size={16} />
                </ThemeIcon>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600}>
                    {item.title}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {item.body}
                  </Text>
                </Box>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Zeigt nach dem Login die Einträge seit dem letzten Besuch; über
 * `openChangelog()` lässt sich jederzeit die vollständige Liste öffnen.
 */
export function ChangelogModal() {
  const { user, isAuthenticated } = useAuthStore();
  const isMobile = useMediaQuery('(max-width: 47.99em)');

  const [opened, setOpened] = useState(false);
  /** true = manuell geöffnet → gesamte Historie statt nur der Neuigkeiten. */
  const [showAll, setShowAll] = useState(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'TEAMLEAD';
  const key = storageKey(user?.id);

  const visibleEntries = useMemo(
    () => CHANGELOG.filter((e) => !e.adminOnly || isAdmin),
    [isAdmin],
  );

  const [unseen, setUnseen] = useState<ChangelogEntry[]>([]);

  // Beim Login prüfen, ob seit dem letzten Besuch etwas dazugekommen ist.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const lastSeen = readLastSeen(key);
    const fresh = getUnseenEntries(lastSeen).filter((e) => !e.adminOnly || isAdmin);
    if (fresh.length === 0) return;
    setUnseen(fresh);
    setShowAll(false);
    setOpened(true);
  }, [isAuthenticated, user, key, isAdmin]);

  // Manuelles Öffnen aus Menü, Seitenleiste oder Hilfe.
  useEffect(() => {
    const handler = () => {
      setShowAll(true);
      setOpened(true);
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const handleClose = useCallback(() => {
    setOpened(false);
    // Immer auf die laufende App-Version setzen, nicht auf den obersten Eintrag:
    // sonst poppt ein Release ohne Changelog-Eintrag beim nächsten Update erneut auf.
    const newest = CHANGELOG[0]?.version ?? __APP_VERSION__;
    const target =
      compareVersions(__APP_VERSION__, newest) >= 0 ? __APP_VERSION__ : newest;
    writeLastSeen(key, target);
    setUnseen([]);
  }, [key]);

  const entries = showAll ? visibleEntries : unseen;
  if (entries.length === 0) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="lg"
      fullScreen={isMobile}
      scrollAreaComponent={ScrollArea.Autosize}
      title={
        <Group gap="xs">
          <ThemeIcon size="md" radius="md" variant="light" color="orange">
            <IconSparkles size={16} />
          </ThemeIcon>
          <div>
            <Text fw={600}>{showAll ? 'Änderungen im ServiceHub' : 'Was ist neu'}</Text>
            <Text size="xs" c="dimmed">
              Du bist auf Version {__APP_VERSION__}
            </Text>
          </div>
        </Group>
      }
    >
      <Stack gap="md">
        {!showAll && (
          <Text size="sm" c="dimmed">
            Seit deinem letzten Besuch hat sich das hier geändert. Du findest die Liste
            jederzeit wieder über dein Benutzermenü oben rechts.
          </Text>
        )}

        {entries.map((entry, i) => {
          // Monats-Trenner nur in der Voll-Ansicht: bei drei neuen Einträgen
          // nach dem Login wäre er Dekoration, bei 35 ist er Orientierung.
          const month = monthLabel(entry.date);
          const showMonth = showAll && (i === 0 || monthLabel(entries[i - 1].date) !== month);
          return (
            <div key={`${entry.version}-${entry.title}`}>
              {showMonth && (
                <Divider
                  my="xs"
                  labelPosition="left"
                  label={
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                      {month}
                    </Text>
                  }
                />
              )}
              <EntryCard entry={entry} isAdmin={isAdmin} />
            </div>
          );
        })}

        {!showAll && unseen.length < visibleEntries.length && (
          <Button variant="subtle" size="sm" onClick={() => setShowAll(true)}>
            Ältere Änderungen anzeigen
          </Button>
        )}

        <Divider />

        <Group justify="flex-end">
          <Button onClick={handleClose}>Verstanden</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
